import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { ApiError } from './api-error-handler.service';

type MessageParameters = Record<string, string>;
type MPFContext = { translate: TranslateService };
type MessageParametersFactory = (error: ApiError, context: MPFContext) => MessageParameters;

@Injectable({
    providedIn: 'root',
})
export class ApiErrorMessageFormatterService {
    private readonly messageParametersFactories: ReadonlyMap<string, MessageParametersFactory> = new Map([
        ['INT-004', getInt004MessageParameters],
    ]);

    constructor(private readonly translate: TranslateService) {}

    public getMessage(error: ApiError): string {
        const messageKey = `apiErrors.${error.code}_message`;
        const parametersFactory = this.messageParametersFactories.get(error.code);
        if (parametersFactory === undefined) {
            return this.translate.instant(messageKey);
        }
        return this.translate.instant(messageKey, parametersFactory(error, { translate: this.translate }));
    }
}

/**
 * Builds a human-friendly retry-time phrase (e.g. "in 5 minutes") for messages that
 * include a {{retryText}} placeholder. Falls back to a generic "later" phrase when the
 * error has no (valid) details.retryAfter timestamp.
 */
const getInt004MessageParameters = (error: ApiError, ctx: MPFContext): MessageParameters => {
    let retryText: string;
    const retryAfter = error.details?.retryAfter;
    if (typeof retryAfter !== 'string') {
        retryText = ctx.translate.instant('apiErrors.retry_default_text');
    } else {
        const retryDate = new Date(retryAfter);
        if (isNaN(retryDate.getTime())) {
            retryText = ctx.translate.instant('apiErrors.retry_default_text');
        } else {
            retryText = formatRelativeTime(ctx.translate, retryDate);
        }
    }
    return { retryText };
};

const formatRelativeTime = (translate: TranslateService, retryDate: Date): string => {
    // Round up so the message never suggests retrying before the limit actually resets.
    const diffSeconds = Math.ceil((retryDate.getTime() - Date.now()) / 1000);
    const relativeTimeFormat = new Intl.RelativeTimeFormat(translate.getCurrentLang() || 'en', {
        numeric: 'auto',
    });
    if (diffSeconds <= 0) {
        return translate.instant('apiErrors.retry_now_text');
    }
    if (diffSeconds < 60) {
        return relativeTimeFormat.format(diffSeconds, 'second');
    }
    const diffMinutes = Math.ceil(diffSeconds / 60);
    if (diffMinutes < 60) {
        return relativeTimeFormat.format(diffMinutes, 'minute');
    }
    return relativeTimeFormat.format(Math.ceil(diffMinutes / 60), 'hour');
};
